import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Event, getEvents, cancelEvent } from '../services/api';

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const data = await getEvents();
      setEvents(data);
      setError('');
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchEvents();
  }, []);
  
  const handleCancelEvent = async (eventId: string) => {
    setCancellingId(eventId);
    try {
      await cancelEvent(eventId);
      fetchEvents();
    } catch (err) {
      console.error('Error cancelling event:', err);
      setError('Failed to cancel event. Please try again later.');
    } finally {
      setCancellingId(null);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch(status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'booked':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'primary';
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(date);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Events
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          component={Link}
          to="/events/new"
        >
          Add New Event
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No events found
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            component={Link}
            to="/events/new"
            sx={{ mt: 2 }}
          >
            Add Your First Event
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => (
            <Grid item xs={12} sm={6} md={4} key={event.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                      Event ID: {event.external_event_id}
                    </Typography>
                    <Chip 
                      label={event.status} 
                      color={getStatusColor(event.status)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Last updated: {formatDate(event.updated_at)}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<ViewIcon />}
                    component={Link}
                    to={`/events/${event.id}`}
                  >
                    View Details
                  </Button>
                  {event.status === 'pending' && (
                    <Button 
                      size="small" 
                      color="error" 
                      startIcon={<CancelIcon />}
                      onClick={() => handleCancelEvent(event.id)}
                      disabled={cancellingId === event.id}
                    >
                      {cancellingId === event.id ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EventsPage; 